/**
 * Scroll Reveal (reduced): IntersectionObserver + CSS transitions.
 *
 * Usage:
 * - Single element reveal:
 *   <div class="reveal" data-reveal></div>
 *
 * - Stagger group:
 *   <section data-reveal-stagger="80">
 *     <div class="reveal" data-reveal-item></div>
 *     <div class="reveal" data-reveal-item></div>
 *   </section>
 *
 * Optional attributes:
 * - data-reveal-once="false"        (default: true)
 * - data-reveal-threshold="0.08"    (default: 0.08)
 * - data-reveal-root-margin="0px 0px -20% 0px" (default)
 * - data-reveal-offset="20%"       (shorthand → rootMargin bottom; e.g. 20%, 120px)
 * - data-reveal-stagger="80"        (ms, default: 80)
 * - data-reveal-items=".selector"   (override children selector; default: [data-reveal-item])
 */
export function reveal() {
	// Respect reduced motion: show everything immediately, no observers.
	const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches
	if (prefersReducedMotion) {
		document.querySelectorAll('[data-reveal]').forEach((el) => el.classList.add('is-inview'))
		document.querySelectorAll('[data-reveal-stagger]').forEach((group) => {
			getGroupItems(group).forEach((item) => item.classList.add('is-inview'))
		})
		return
	}

	const defaultOptions = {
		threshold: 0.08,
		rootMargin: '0px 0px -25% 0px',
		once: true,
	}

	// Single items
	const singleTargets = Array.from(document.querySelectorAll('[data-reveal]')).filter((el) => !el.closest('[data-reveal-stagger]'))
	if (singleTargets.length) {
		const observer = new IntersectionObserver(
			(entries, io) => {
				for (const entry of entries) {
					if (!entry.isIntersecting) continue
					const el = entry.target
					el.classList.add('is-inview')
					const once = parseBool(el.getAttribute('data-reveal-once'), defaultOptions.once)
					if (once) io.unobserve(el)
				}
			},
			{
				threshold: defaultOptions.threshold,
				rootMargin: defaultOptions.rootMargin,
			}
		)

		for (const el of singleTargets) {
			if (el.dataset.revealBound) continue
			el.dataset.revealBound = '1'

			const threshold = parseNumber(el.getAttribute('data-reveal-threshold'), defaultOptions.threshold)
			const rootMargin = el.getAttribute('data-reveal-root-margin') || offsetToRootMargin(el.getAttribute('data-reveal-offset')) || defaultOptions.rootMargin

			// If element overrides, use a dedicated observer instance.
			if (threshold !== defaultOptions.threshold || rootMargin !== defaultOptions.rootMargin) {
				const perElObserver = new IntersectionObserver(
					(entries, io) => {
						for (const entry of entries) {
							if (!entry.isIntersecting) continue
							const target = entry.target
							target.classList.add('is-inview')
							const once = parseBool(target.getAttribute('data-reveal-once'), defaultOptions.once)
							if (once) io.unobserve(target)
						}
					},
					{ threshold, rootMargin }
				)
				perElObserver.observe(el)
			} else {
				observer.observe(el)
			}
		}
	}

	// Stagger groups
	const groups = Array.from(document.querySelectorAll('[data-reveal-stagger]'))
	if (groups.length) {
		const groupObserver = new IntersectionObserver(
			(entries, io) => {
				for (const entry of entries) {
					if (!entry.isIntersecting) continue
					const group = entry.target

					const staggerMs = parseNumber(group.getAttribute('data-reveal-stagger'), 80)
					const items = getGroupItems(group)

					items.forEach((item, index) => {
						if (!item.style.getPropertyValue('--reveal-delay')) {
							item.style.setProperty('--reveal-delay', `${Math.max(0, index) * Math.max(0, staggerMs)}ms`)
						}
						item.classList.add('is-inview')
					})

					const once = parseBool(group.getAttribute('data-reveal-once'), defaultOptions.once)
					if (once) io.unobserve(group)
				}
			},
			{
				threshold: defaultOptions.threshold,
				rootMargin: defaultOptions.rootMargin,
			}
		)

		for (const group of groups) {
			if (group.dataset.revealGroupBound) continue
			group.dataset.revealGroupBound = '1'

			const threshold = parseNumber(group.getAttribute('data-reveal-threshold'), defaultOptions.threshold)
			const rootMargin = group.getAttribute('data-reveal-root-margin') || offsetToRootMargin(group.getAttribute('data-reveal-offset')) || defaultOptions.rootMargin

			if (threshold !== defaultOptions.threshold || rootMargin !== defaultOptions.rootMargin) {
				const perGroupObserver = new IntersectionObserver(
					(entries, io) => {
						for (const entry of entries) {
							if (!entry.isIntersecting) continue
							const g = entry.target
							const staggerMs = parseNumber(g.getAttribute('data-reveal-stagger'), 80)
							const items = getGroupItems(g)
							items.forEach((item, index) => {
								if (!item.style.getPropertyValue('--reveal-delay')) {
									item.style.setProperty('--reveal-delay', `${Math.max(0, index) * Math.max(0, staggerMs)}ms`)
								}
								item.classList.add('is-inview')
							})
							const once = parseBool(g.getAttribute('data-reveal-once'), defaultOptions.once)
							if (once) io.unobserve(g)
						}
					},
					{ threshold, rootMargin }
				)
				perGroupObserver.observe(group)
			} else {
				groupObserver.observe(group)
			}
		}
	}
}

function getGroupItems(group) {
	const selector = group.getAttribute('data-reveal-items') || '[data-reveal-item]'
	const items = Array.from(group.querySelectorAll(selector))
	// If someone forgot data-reveal-item, allow a pragmatic fallback.
	if (items.length) return items
	return Array.from(group.children).filter((el) => el instanceof HTMLElement)
}

function parseBool(value, fallback) {
	if (value === null || value === undefined) return fallback
	if (value === '' || value === 'true' || value === '1') return true
	if (value === 'false' || value === '0') return false
	return fallback
}

function parseNumber(value, fallback) {
	if (value === null || value === undefined || value === '') return fallback
	const n = Number(value)
	return Number.isFinite(n) ? n : fallback
}

function offsetToRootMargin(offset) {
	if (!offset) return null
	const raw = String(offset).trim()
	if (!raw) return null

	// Allow "20" (treated as px), "20px", "20%", "-10%" (won't be negated twice)
	const pxMatch = raw.match(/^(-?\d+(?:\.\d+)?)px$/i)
	const percentMatch = raw.match(/^(-?\d+(?:\.\d+)?)%$/)
	const numberMatch = raw.match(/^(-?\d+(?:\.\d+)?)$/)

	let bottom = null
	if (pxMatch) bottom = `${pxMatch[1]}px`
	else if (percentMatch) bottom = `${percentMatch[1]}%`
	else if (numberMatch) bottom = `${numberMatch[1]}px`
	else return null

	// We use negative bottom margin to trigger earlier.
	// If user passes "-20%", keep it as-is; if "20%" → "-20%".
	const normalized = bottom.startsWith('-') ? bottom : `-${bottom}`
	return `0px 0px ${normalized} 0px`
}

